import type { ApprovalMethod } from "@entrilia/shared/api/workflowTypes";
import type { StateAction } from "@entrilia/shared/types";
import type { Approval, ApproverAssignment } from "../../../api/types/approvalTypes";
import type { WorkflowApproval } from "../../../api/types/workflowTypes";

export interface ApproversAssignmentState {
  steps: ApproversAssignmentStep[];
}

export interface ApproversAssignmentStep {
  name: string;
  rules: ApproversAssignmentRule[];
}

interface ApproversAssignmentRule {
  ruleIndex: number;
  approvalMethod: ApprovalMethod;
  users: ApproversAssignmentUser[];
}

interface ApproversAssignmentUser {
  id: string;
  name: string;
  selected: boolean;
}

// Initial assignment by workflow requestor
export const getInitialApproversAssignmentStateFromApprovalPreview = (
  approvalPreview: WorkflowApproval
): ApproversAssignmentState => {
  const steps = approvalPreview.approvalStepStates
    .filter((step) => step.status === "Pending")
    .map((step) => ({
      name: step.stepName,
      rules: step.rules
        .filter((rule) => rule.status === "Pending")
        .map((rule) => ({
          ruleIndex: rule.ruleIndex,
          approvalMethod: rule.method,
          users: rule.pendingApprovalFromUsers.map((user) => ({
            id: user.userId,
            name: user.name,
            selected: false,
          })),
        }))
        .filter((rule) => rule.users.length > 0),
    }))
    .filter((step) => step.rules.length > 0);

  return {
    steps,
  };
};

// Reassign by approver user
export const getInitialApproversAssignmentStateFromPendingApproval = (
  approval: Approval,
  currentUserId: string
): ApproversAssignmentState => {
  const currentStep = approval.approvalStepStates.find(
    (step) => step.stepName === approval.currentApprovalStep && step.status === "Pending"
  );
  if (!currentStep) {
    return {
      steps: [],
    };
  }

  const assignmentStep = {
    name: currentStep.stepName,
    rules: currentStep.rules
      .filter(
        (rule) =>
          rule.status === "Pending" && rule.pendingApprovalFromUsers.some((user) => user.userId === currentUserId)
      )
      .map((rule) => ({
        ruleIndex: rule.ruleIndex,
        approvalMethod: rule.method,
        users: rule.eligibleApproverUsers.map((user) => ({
          id: user.userId,
          name: user.name,
          selected: rule.pendingApprovalFromUsers.some((assignedUser) => assignedUser.userId === user.userId),
        })),
      }))
      .filter((rule) => rule.users.length > 0),
  };

  if (assignmentStep.rules.length === 0) {
    return {
      steps: [],
    };
  }

  return {
    steps: [assignmentStep],
  };
};

export const mapStateToApproverAssignments = (state: ApproversAssignmentState): ApproverAssignment[] => {
  const assignments: ApproverAssignment[] = [];

  for (const step of state.steps) {
    for (const rule of step.rules) {
      assignments.push({
        userIds: rule.users.filter((user) => user.selected).map((user) => user.id),
        approvalStepName: step.name,
        approvalRuleIndex: rule.ruleIndex,
      });
    }
  }

  return assignments;
};

export interface ApproverAssignmentValidationError {
  stepName: string;
  ruleIndex: number;
}

export const getApproversAssignmentStateValidationErrors = (
  state: ApproversAssignmentState
): ApproverAssignmentValidationError[] => {
  const errors: ApproverAssignmentValidationError[] = [];

  for (const step of state.steps) {
    for (const rule of step.rules) {
      if (rule.users.every((user) => !user.selected)) {
        errors.push({ stepName: step.name, ruleIndex: rule.ruleIndex });
      }
    }
  }

  return errors;
};

// State Actions

export const changeApproversAssignmentUserSelectionAction =
  (stepName: string, ruleIndex: number, userId: string): StateAction<ApproversAssignmentState> =>
  (state) => {
    const newSteps = state.steps.map((step) => {
      const newRules = step.rules.map((rule) => {
        const newUsers = rule.users.map((user) => {
          if (step.name === stepName && rule.ruleIndex === ruleIndex && user.id === userId) {
            return { ...user, selected: !user.selected };
          }
          return { ...user };
        });

        return { ...rule, users: newUsers };
      });
      return { ...step, rules: newRules };
    });

    return { ...state, steps: newSteps };
  };
